import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { accountService } from '../../../services/accountService';

interface ProfileImageUploadProps {
  currentImageUrl: string | null;
  userInitials: string;
  onImageChange: (imageUrl: string) => void;
  onImageDelete: () => void;
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  currentImageUrl,
  userInitials,
  onImageChange,
  onImageDelete
}) => {
  const uiData = useSelector((state: RootState) => state.ui);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate consistent color from initials
  const getInitialsColor = (initials: string): string => {
    let hash = 0;
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 60%, 50%)`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(png|jpeg|jpg)/)) {
      setError('Only PNG and JPEG images are allowed');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError(null);
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);

    try {
      // Get presigned upload URL
      const urlResponse = await accountService.getImageUploadUrl();
      if (!urlResponse.success || !urlResponse.data) {
        throw new Error(urlResponse.error || 'Failed to get upload URL');
      }

      const { uploadUrl, imageUrl } = urlResponse.data;

      // Upload to S3
      const uploadResponse = await accountService.uploadImageToS3(selectedFile, uploadUrl);
      if (!uploadResponse.success) {
        throw new Error(uploadResponse.error || 'Failed to upload image');
      }

      // Success - update parent component
      onImageChange(imageUrl);
      setPreviewUrl(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadCancel = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteConfirm = async () => {
    setIsUploading(true);
    setError(null);

    try {
      const response = await accountService.deleteImage();
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete image');
      }

      onImageDelete();
      setShowDeleteModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2vh'
    }}>
      {/* Avatar Display */}
      <div style={{
        width: '15vh',
        height: '15vh',
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: previewUrl || currentImageUrl ? 'transparent' : getInitialsColor(userInitials),
        border: `0.3vh solid rgba(${uiData.schemeColor}, 0.5)`,
        fontSize: '6vh',
        fontFamily: 'Oswald',
        fontWeight: '600',
        color: 'white'
      }}>
        {previewUrl || currentImageUrl ? (
          <img
            src={previewUrl || currentImageUrl || ''}
            alt="Profile"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span>{userInitials}</span>
        )}
      </div>

      {/* Upload Controls */}
      {!previewUrl && (
        <div style={{ display: 'flex', gap: '1vh' }}>
          <label style={{
            backgroundColor: `rgba(${uiData.schemeColor}, 0.2)`,
            border: `0.2vh solid rgb(${uiData.schemeColor})`,
            padding: '1vh 2vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            fontWeight: '600',
            borderRadius: '0.75vh',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}>
            {currentImageUrl ? 'Change Image' : 'Upload Image'}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </label>

          {currentImageUrl && (
            <button
              onClick={() => setShowDeleteModal(true)}
              style={{
                backgroundColor: 'rgba(200, 60, 60, 0.2)',
                border: '0.2vh solid rgb(200, 60, 60)',
                padding: '1vh 2vh',
                fontSize: '1.5vh',
                fontFamily: 'Noto Sans',
                fontWeight: '600',
                borderRadius: '0.75vh',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              Delete Image
            </button>
          )}
        </div>
      )}

      {/* Preview Controls */}
      {previewUrl && (
        <div style={{ display: 'flex', gap: '1vh' }}>
          <button
            onClick={handleUploadConfirm}
            disabled={isUploading}
            style={{
              backgroundColor: `rgba(${uiData.schemeColor}, 0.2)`,
              border: `0.2vh solid rgb(${uiData.schemeColor})`,
              padding: '1vh 2vh',
              fontSize: '1.5vh',
              fontFamily: 'Noto Sans',
              fontWeight: '600',
              borderRadius: '0.75vh',
              cursor: isUploading ? 'wait' : 'pointer',
              transition: 'all 0.3s ease',
              color: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            {isUploading ? 'Uploading...' : 'Confirm Upload'}
          </button>
          <button
            onClick={handleUploadCancel}
            disabled={isUploading}
            style={{
              backgroundColor: 'rgba(60, 60, 60, 0.8)',
              border: '0.2vh solid rgba(90, 90, 90, 0.8)',
              padding: '1vh 2vh',
              fontSize: '1.5vh',
              fontFamily: 'Noto Sans',
              fontWeight: '600',
              borderRadius: '0.75vh',
              cursor: isUploading ? 'wait' : 'pointer',
              transition: 'all 0.3s ease',
              color: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          color: 'rgb(255, 100, 100)',
          fontSize: '1.5vh',
          fontFamily: 'Noto Sans',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'rgba(40, 40, 40, 0.95)',
            padding: '4vh',
            borderRadius: '1vh',
            border: `0.2vh solid rgba(${uiData.schemeColor}, 0.5)`,
            maxWidth: '40vh',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '2.5vh',
              fontFamily: 'Oswald',
              fontWeight: '500',
              marginBottom: '2vh',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              Delete Profile Image?
            </div>
            <div style={{
              fontSize: '1.5vh',
              fontFamily: 'Noto Sans',
              marginBottom: '3vh',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              This action cannot be undone. Your profile will show your initials instead.
            </div>
            <div style={{ display: 'flex', gap: '1vh', justifyContent: 'center' }}>
              <button
                onClick={handleDeleteConfirm}
                disabled={isUploading}
                style={{
                  backgroundColor: 'rgba(200, 60, 60, 0.3)',
                  border: '0.2vh solid rgb(200, 60, 60)',
                  padding: '1vh 2vh',
                  fontSize: '1.5vh',
                  fontFamily: 'Noto Sans',
                  fontWeight: '600',
                  borderRadius: '0.75vh',
                  cursor: isUploading ? 'wait' : 'pointer',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}
              >
                {isUploading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isUploading}
                style={{
                  backgroundColor: 'rgba(60, 60, 60, 0.8)',
                  border: '0.2vh solid rgba(90, 90, 90, 0.8)',
                  padding: '1vh 2vh',
                  fontSize: '1.5vh',
                  fontFamily: 'Noto Sans',
                  fontWeight: '600',
                  borderRadius: '0.75vh',
                  cursor: isUploading ? 'wait' : 'pointer',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileImageUpload;
